import { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Modal,
  Typography,
  Button,
  Space,
  Form,
  Input,
  Grid,
  Alert,
  Progress,
} from "antd";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { setAccountPW, logoutUser } from "../../apiBridge";
import { MessageInstance } from "antd/es/message/interface";

interface Props {
  isOpen: boolean;
  controller: React.Dispatch<React.SetStateAction<boolean>>;
  username: string;
  messageController: MessageInstance;
  isOwner: boolean;
}

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;
const zxcvbn = require("zxcvbn");

const onFinishFailed = (errorInfo: any) => {
  console.log("Failed:", errorInfo);
};

const ResetPWModal: React.FC<Props> = ({
  isOpen,
  controller,
  username,
  messageController,
  isOwner,
}) => {
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const [form] = Form.useForm();

  const [loadingState, setLoadingState] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState("");
  const [passwordScore, setPasswordScore] = useState(0);
  const [newPW, setNewPW] = useState("");


  console.log("Settings modal");

  const onSubmitResetPassword = async (values: any) => {
    if (values.current_password === values.new_password) {
      setShowErrorAlert("please specify a new password")
      return
    }

    setLoadingState(true);
    const res = await setAccountPW({
      username,
      email: username,
      password: values.current_password,
      new_password: values.new_password,
      mode: isOwner ? "owner" : "member",
    });
    setLoadingState(false);
    if (res.status === 200) {
      controller(false);
      messageController.success("Successfully updated password");

      setTimeout(() => {
        logoutUser();
        navigate("/login")
      }, 1000);
      
    } else {
      const errBody = await res.json();
      setShowErrorAlert(errBody && errBody.error && errBody.error !== "" ? errBody.error : "Invalid current password or new password");
    }
  };

  return (
    <Modal
      footer={null}
      maskClosable={false}
      closeIcon={false}
      centered
      open={isOpen}
      width={380}
      afterOpenChange={(open) => {
        if (!open) {
          setShowErrorAlert('');
          form.resetFields();
          setNewPW("");
          setPasswordScore(0);
        }
      }}
    >
      <div style={{ marginBottom: 20 }}>
        <Title level={4}>Change Password</Title>
        <Text type="secondary">
          Protect your account with a unique password of at least 8 characters
          long
        </Text>
      </div>
      {showErrorAlert !== "" && (
        <Alert
          message={ showErrorAlert }
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
        />
      )}
      <Form
        name="edituserform"
        layout="vertical"
        onFinish={onSubmitResetPassword}
        onFinishFailed={onFinishFailed}
        autoComplete="off"
        requiredMark={false}
        form={form}
      >
        <Form.Item
          label={screens.md ? "Current Password" : ""}
          name="current_password"
          rules={[
            { required: true, message: "Please input your current password!" },
          ]}
        >
          <Input.Password placeholder="Current Password" />
        </Form.Item>

        <Form.Item
          label={screens.md ? "New Password" : ""}
          name="new_password"
          rules={[
            {},
            ({ getFieldValue }) => ({
              validator(_, value) {
                const score = zxcvbn(value).score;
                console.info("password score", score)
                setNewPW(value);
                setPasswordScore(score);
                if (
                  !value ||
                  value.length < 8 ||
                  !/^(?=.*[a-zA-Z])(?=.*[0-9]).+$/.test(value)
                ) {
                  return Promise.reject();
                } else if (score < 2) {
                  return Promise.reject(new Error("password too weak"));
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <div>
            <Input.Password placeholder="New Password" />
            <Progress
              percent={Math.max(passwordScore * 25, 1)}
              showInfo={false}
              strokeColor={
                passwordScore * 25 < 50
                  ? "#ff4d4f"
                  : passwordScore * 25 < 75
                  ? "#ff9e07"
                  : "#00b7b2"
              }
            />
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Strength:{" "}
                <Text type="secondary" style={{ fontSize: "inherit" }}>
                  {passwordScore === 0
                    ? "VERY WEAK"
                    : passwordScore === 1
                    ? "VERY WEAK"
                    : passwordScore === 2
                    ? "WEAK"
                    : passwordScore === 3
                    ? "FAIR"
                    : "STRONG"}
                </Text>
              </Text>
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Your password must include:
              </Text>
            </div>
            <div>
              {/^(?=.*[a-zA-Z]).+$/.test(newPW) ? (
                <CheckCircleOutlined style={{ color: "#00b7b2" }} />
              ) : (
                <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
              )}
              &nbsp;&nbsp;
              <Text type="secondary" style={{ fontSize: 12 }}>
                At least 1 letter
              </Text>
            </div>
            <div>
              {/^(?=.*[0-9]).+$/.test(newPW) ? (
                <CheckCircleOutlined style={{ color: "#00b7b2" }} />
              ) : (
                <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
              )}
              &nbsp;&nbsp;
              <Text type="secondary" style={{ fontSize: 12 }}>
                At least 1 number
              </Text>
            </div>
            <div>
              {newPW.length >= 8 ? (
                <CheckCircleOutlined style={{ color: "#00b7b2" }} />
              ) : (
                <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
              )}
              &nbsp;&nbsp;
              <Text type="secondary" style={{ fontSize: 12 }}>
                Minimum of 8 characters
              </Text>
            </div>
            <div>
              {passwordScore > 1 ? (
                <CheckCircleOutlined style={{ color: "#00b7b2" }} />
              ) : (
                <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
              )}
              &nbsp;&nbsp;
              <Text type="secondary" style={{ fontSize: 12 }}>
                Minimum WEAK password strength
              </Text>
            </div>
          </div>
        </Form.Item>
        <Form.Item
          label={screens.md ? "Retype password" : ""}
          name="retypepassword"
          dependencies={["new_password"]}
          rules={[
            {},
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value && getFieldValue("new_password")) {
                  return Promise.reject(
                    new Error("The new password that you entered do not match!")
                  );
                } else if (!value) {
                  return Promise.resolve();
                } else if (getFieldValue("new_password") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error("The new password that you entered do not match!")
                );
              },
            }),
          ]}
        >
          <Input.Password placeholder="Retype password" />
        </Form.Item>

        <div style={{ textAlign: "right" }}>
          <Space>
            <Button onClick={() => controller(false)}>Cancel</Button>

            <Form.Item style={{ margin: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loadingState}
                disabled={
                  !/^(?=.*[a-zA-Z])(?=.*[0-9]).+$/.test(newPW) ||
                  newPW.length < 8
                }
              >
                Change Password
              </Button>
            </Form.Item>
          </Space>
        </div>
      </Form>
    </Modal>
  );
};

export default ResetPWModal;
